<script setup lang="ts">
import { useStore } from 'vuex';
import { computed } from 'vue';
import { ENTITY_TYPE, ID_TYPE } from '@clearbanc/data-common-types';

import DropdownGroup from '@/components/forms/groups/DropdownGroup.vue';
import UploadFileWithDownloadableFileList from '@/components/upload/UploadFileWithDownloadableFileList.vue';
import { useForm } from 'vee-validate';
import { UserResponseDto } from '@clearbanc/baus-sdk';
import { FileUpload } from '@/types/file-upload.types';

import OnboardingInvitedButtons from './components/OnboardingInvitedButtons.vue';

interface UserIdUploadForm {
  type: ID_TYPE;
}

const fileTypes = [
  {
    value: ID_TYPE.DRIVERS_LICENSE,
    name: 'Driver’s license',
  },
  {
    value: ID_TYPE.PASSPORT,
    name: 'Passport',
  },
];

const store = useStore();

const user = computed<UserResponseDto>(() => store.getters.user);
const uploads = computed<FileUpload[]>(() => store.getters.uploads ?? []);

const getInitialFormValues = () => {
  const hasPassport = uploads.value.some(
    (upload) => upload.type === ID_TYPE.PASSPORT,
  );
  const type = hasPassport ? ID_TYPE.PASSPORT : ID_TYPE.DRIVERS_LICENSE;

  return {
    type,
  };
};

const { values } = useForm<UserIdUploadForm>({
  initialValues: getInitialFormValues(),
});

const fileFilter = computed(() => {
  return {
    metaId: user.value.id,
    entity: ENTITY_TYPE.OWNER,
    type: values.type,
  };
});

const uploadedFiles = computed(() => {
  return store.getters.uploadsMatching({
    metaId: user.value.id,
    type: [ID_TYPE.PASSPORT, ID_TYPE.DRIVERS_LICENSE],
  });
});

async function removeUpload(id: number) {
  await store.dispatchApiAction('DELETE_USER_UPLOAD', { id });
}
</script>

<template>
  <div class="onboarding-form">
    <p>Upload the front and back of your driver's license or passport</p>
    <form class="c-form">
      <div class="c-form__row">
        <DropdownGroup
          name="type"
          label="File Type"
          :options="fileTypes"
          :reset-filter-on-hide="true"
          option-label="name"
          option-value="value"
        />
      </div>
      <div class="c-form__row">
        <UploadFileWithDownloadableFileList
          ref="uploadFileList"
          :upload-filters="fileFilter"
          :empty-prompt="$t('common.messagePrompt.dragAndDropFilesOrBrowse')"
          theme="onboarding"
          no-list
          :files="uploadedFiles"
          @fileRemove="removeUpload"
        />
      </div>
    </form>
    <OnboardingInvitedButtons :disabled="!uploadedFiles.length" />
  </div>
</template>

<style scoped lang="less">
.onboarding-form {
  max-width: 600px;
  margin: 0 auto;
  padding: 2rem 1rem;

  p {
    text-align: left;
    font-family: @regular-font;
    font-weight: 500;
    font-size: 16px;
    letter-spacing: 0;
  }
}
</style>
